// ./src/utils/topUp.ts
//
// The two decisions behind a top-up purchase, as PURE functions.
//
// ⭐ Extracted for the same reason `planUsage.ts` and `planEntitlement.ts` were: they live in a
// React hook otherwise, where no script can reach them — and both are invisible when wrong. A
// wrong answer here does not throw; it shows a correct-looking list of the wrong thing.
import { GENERATED_FEATURE_CODES as FEATURE_CODES } from "@/config/featureCodes.generated";

/**
 * What a top-up drawer is selling.
 *
 * ⚠ TWO *SENDING* COUNTERS, NEVER THREE. SMS and WhatsApp both spend `message_credits` — the
 * server does `kind = channel === 'email' ? 'email' : 'message'`. A "WhatsApp credit" and an
 * "SMS credit" have never existed, and both have been offered for sale in this app by mistake.
 *
 * ⚠ `ai` IS NOT A THIRD SENDING COUNTER — it is a different feature entirely (`ai_credits`),
 * sold on the Subscription page and nowhere near a message. It is in this union because the
 * drawer sells it, NOT because the messaging model grew a third counter. Keep that distinction:
 * the rule above is about what a SEND spends, and nothing about AI touches it.
 */
export type TopUpKind = "email" | "message" | "ai";

/** Every kind, for guards and for callers that offer a choice. */
export const TOP_UP_KINDS: TopUpKind[] = ["email", "message", "ai"];

/**
 * Which kind a top-up drawer should sell, given an optional caller-supplied override.
 *
 * ⚠ THIS VALIDATES RATHER THAN DEFAULTING, and it is standing on a bug that was written and
 * caught rather than a hypothetical. The hook's `open` callback is the natural thing to hand
 * straight to a Button (`onTopUp={topUp.open}`) — and React then calls it with a CLICK EVENT as
 * the argument. A plain `override ?? fallback` keeps the event, every `=== "email"` comparison
 * downstream is false, and the drawer loads MESSAGE bundles for an EMAIL send: the host stays
 * blocked, looking at a perfectly plausible list of the wrong bundles.
 *
 * ⚠ `tsc` cannot see it. The consuming props are typed `() => void`, and a function with an
 * optional parameter is assignable to that. So the check has to exist at runtime.
 */
export const resolveTopUpKind = (override: unknown, fallback: TopUpKind): TopUpKind => {
  // ⚠ Membership in the declared list, not a hand-written chain of `===`. When `ai` was added
  // the chain would have silently rejected it and fallen back — a caller asking for AI bundles
  // would have been handed message bundles, which is exactly the failure this guard exists to
  // stop, reintroduced by the guard itself.
  if (typeof override === "string" && (TOP_UP_KINDS as string[]).includes(override)) {
    return override as TopUpKind;
  }

  return fallback;
};

/**
 * The CREDIT feature code to ask the catalog for.
 *
 * ⚠ `email_credits`, NOT `email_qty`. The `_qty` features are the monthly allowance and cannot
 * be bought; asking the catalog for one returns an empty list, which renders as "no top-up
 * bundles are available on this account" — a dead end that reads as a server problem rather
 * than a wrong argument.
 */
export const creditFeatureForKind = (kind: TopUpKind): string => {
  if (kind === "email") {
    return FEATURE_CODES.EMAIL_CREDITS;
  }

  if (kind === "ai") {
    return FEATURE_CODES.AI_CREDITS;
  }

  return FEATURE_CODES.MESSAGE_CREDITS;
};

/**
 * The reverse: which kind sells a given CREDIT feature code, or null if none does.
 *
 * ⚠ Exists so a caller that has a feature code (the Subscription page, whose meters are built
 * from the declared `FEATURE_TOP_UPS` map) can offer the right drawer WITHOUT hard-coding a
 * second copy of the pairing. A second copy is how the page ends up selling message bundles on
 * the email tile the day a fourth metered feature appears.
 */
export const kindForCreditFeature = (creditFeatureCode: string): TopUpKind | null => {
  return TOP_UP_KINDS.find((k) => creditFeatureForKind(k) === creditFeatureCode) ?? null;
};

/** The fields the purchasable ordering depends on. Structural, so a script can exercise it. */
export interface SortablePurchasable {
  amount?: number | null;
  credits?: number | null;
  name?: string | null;
}

/**
 * The order bundles and services are shown in: **cheapest first**.
 *
 * ⚠ SORTED BY PRICE, NOT BY CREDITS — and the difference is what this function is for. The old
 * rule sorted on `credits`, which was a PROXY for price that held only while every product in a
 * list granted a different number of units. Services broke it immediately: "Quick start call"
 * and "Event setup" both grant 1, so they tied, and a tie fell back to whatever order the server
 * happened to return — which put the £199 setup above the £49 call. The stated intent was always
 * cheapest-first; this sorts on the thing that actually means cheap.
 *
 * ⚠ TOTALLY ORDERED, so the list cannot shuffle between loads. Price, then units, then name —
 * the last is what guarantees determinism, because two products can legitimately share both a
 * price and a unit count. An unstable list on a screen that takes money reads as a glitch and
 * costs trust at the worst moment.
 *
 * ⚠ UNPRICED PRODUCTS SORT LAST, whatever else they are. `amount` is null when the SKU has no
 * price in this Stripe mode; those render disabled ("not available for purchase yet"), and a
 * disabled row at the top of a list of things to buy is a dead end where the primary choice
 * should be. They are shown rather than hidden — a bundle that vanishes makes a host wonder
 * where it went — but they belong at the bottom.
 */
export const sortPurchasables = <T extends SortablePurchasable>(items: T[]): T[] => {
  return items.slice().sort((a, b) => {
    const priceA = a.amount ?? null;
    const priceB = b.amount ?? null;

    // Unpriced last, and two unpriced fall through to the tie-breakers below.
    if (priceA == null && priceB != null) {
      return 1;
    }

    if (priceB == null && priceA != null) {
      return -1;
    }

    if (priceA != null && priceB != null && priceA !== priceB) {
      return priceA - priceB;
    }

    const creditsA = a.credits ?? 0;
    const creditsB = b.credits ?? 0;

    if (creditsA !== creditsB) {
      return creditsA - creditsB;
    }

    return String(a.name ?? "").localeCompare(String(b.name ?? ""));
  });
};
